import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiProblem, resolveApiProblem } from '../utils/api-problem.util';
import { ToastService } from './toast.service';

/** `ApiProblem` com título e mensagem já garantidos (padrão por status quando o backend não mandou). */
export interface ApiProblemView extends ApiProblem {
  title: string;
  detail: string;
}

const STATUS_TITLE_KEYS = new Set([400, 403, 404, 409]);

/**
 * Apresentação dos erros da API: toast com título + mensagem + "Ver mais" e o estado do modal com
 * a lista completa. Quem chama é o `errorInterceptor`; o modal (`app-api-error-modal`) só lê `current`.
 */
@Injectable({ providedIn: 'root' })
export class ApiErrorService {
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  /** Erro exibido no modal; `null` = modal fechado. */
  readonly current = signal<ApiProblemView | null>(null);

  /** Reporta a falha de uma requisição: reserva a supressão do toast da tela e exibe o toast da API. */
  report(error: HttpErrorResponse): void {
    // Síncrono, antes do catchError da tela. O parse pode ser assíncrono (corpo Blob).
    this.toast.reserveApiErrorSuppression();
    resolveApiProblem(error)
      .then((problem) => this.present(this.toView(problem)))
      .catch(() => this.present(this.toView({ status: error.status, title: null, detail: null, errors: [], traceId: null, instance: null })));
  }

  openDetails(problem: ApiProblemView): void {
    this.current.set(problem);
  }

  closeDetails(): void {
    this.current.set(null);
  }

  /** Há algo a mostrar além do que o toast já exibe (mais de um erro, ou código de rastreio)? */
  hasMoreDetails(problem: ApiProblemView): boolean {
    return problem.errors.length > 1 || !!problem.traceId;
  }

  private present(problem: ApiProblemView): void {
    const action = this.hasMoreDetails(problem)
      ? { label: this.translate.instant('common.apiError.seeMore'), handler: () => this.openDetails(problem) }
      : undefined;
    this.toast.showApiProblem(problem.detail, problem.title, action);
  }

  private toView(problem: ApiProblem): ApiProblemView {
    const isServerError = problem.status >= 500;
    const titleKey = STATUS_TITLE_KEYS.has(problem.status)
      ? String(problem.status)
      : isServerError
        ? 'server'
        : 'default';
    const detailKey = isServerError ? 'serverDetail' : 'genericDetail';
    const detail = problem.detail ?? this.translate.instant(`common.apiError.${detailKey}`);
    return {
      ...problem,
      title: problem.title ?? this.translate.instant(`common.apiError.titles.${titleKey}`),
      detail,
      errors: problem.errors.length ? problem.errors : [detail],
    };
  }
}
